/**
 * Chat Window Component
 */

import React, { useState } from 'react';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import type { Message } from '../types';

interface ChatWindowProps {
  messages: Message[];
  isConnected: boolean;
  isTyping: boolean;
  agentName: string | null;
  onSendMessage: (content: string) => Promise<void>;
  onMinimize: () => void;
  onClose: () => void;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  isConnected,
  isTyping,
  agentName,
  onSendMessage,
  onMinimize,
  onClose,
}) => {
  const [isSending, setIsSending] = useState(false);

  const handleSendMessage = async (content: string) => {
    if (!content.trim() || isSending) return;

    setIsSending(true);
    try {
      await onSendMessage(content);
    } catch (error) {
      console.error('[WebChat] Error sending message:', error);
      // TODO: Show error to user
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex flex-col w-[380px] h-[600px] bg-white rounded-lg shadow-2xl animate-slide-up overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary text-white">
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-blue-400'}`} />
          <div>
            <h3 className="font-semibold text-sm">{agentName || 'Asistente Virtual'}</h3>
            <p className="text-xs opacity-90">{isConnected ? 'En línea' : 'Listo para chatear'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onMinimize}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Minimizar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Cerrar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-hidden">
        <MessageList messages={messages} isTyping={isTyping} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200">
        <MessageInput onSendMessage={handleSendMessage} disabled={isSending} />
      </div>
    </div>
  );
};
